import { useEffect, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { FirebaseError } from 'firebase/app';
import { useAuth } from '../auth/AuthProvider';

export function LoginPage() {
  const { user, signInWithGoogle, signInWithEmail, registerWithEmail } = useAuth();
  const navigate = useNavigate();

  // Route onward once authenticated. The mobile Google flow uses a full-page redirect,
  // so it returns to /login in a fresh page where run()'s post-sign-in navigate never
  // runs — react to auth state instead, which also covers the popup/email paths.
  useEffect(() => {
    if (user) navigate('/', { replace: true });
  }, [user, navigate]);
  const [mode, setMode] = useState<'signin' | 'register'>('signin');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const run = async (fn: () => Promise<void>) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
      navigate('/', { replace: true });
    } catch (err) {
      setError(err instanceof FirebaseError ? err.message : 'Something went wrong');
    } finally {
      setBusy(false);
    }
  };

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    void run(() =>
      mode === 'signin' ? signInWithEmail(email, password) : registerWithEmail(email, password),
    );
  };

  return (
    <div className="page-content d-flex align-items-center justify-content-center min-vh-100 bg-light">
      <div className="login-form" style={{ width: '100%', maxWidth: 400 }}>
        <div className="card mb-0">
          <div className="card-body">
            <div className="text-center mb-3">
              <h3 className="mb-0 fw-bold">Jordan</h3>
              <p className="text-muted mb-0">
                {mode === 'signin' ? 'Sign in to your account' : 'Create your account'}
              </p>
            </div>

            {error && (
              <div className="alert alert-danger" role="alert">
                {error}
              </div>
            )}

            <button
              type="button"
              className="btn btn-outline-secondary w-100 mb-3"
              disabled={busy}
              onClick={() => void run(signInWithGoogle)}
            >
              <i className="ph-google-logo me-2" />
              Continue with Google
            </button>

            <div className="text-center text-muted mb-3">
              <small>or use your email</small>
            </div>

            <form onSubmit={onSubmit}>
              <div className="mb-3">
                <label className="form-label">Email</label>
                <input
                  type="email"
                  className="form-control"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  autoComplete="email"
                />
              </div>
              <div className="mb-3">
                <label className="form-label">Password</label>
                <input
                  type="password"
                  className="form-control"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  minLength={6}
                  autoComplete={mode === 'signin' ? 'current-password' : 'new-password'}
                />
              </div>
              <button type="submit" className="btn btn-primary w-100" disabled={busy}>
                {busy ? 'Please wait…' : mode === 'signin' ? 'Sign in' : 'Create account'}
              </button>
            </form>

            <div className="text-center mt-3">
              <button
                type="button"
                className="btn btn-link btn-sm"
                onClick={() => {
                  setMode((m) => (m === 'signin' ? 'register' : 'signin'));
                  setError(null);
                }}
              >
                {mode === 'signin'
                  ? "Don't have an account? Register"
                  : 'Already have an account? Sign in'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
